import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { map, get } from "lodash";
import Assignment from "components/AdminBooks/Assignments";
import { useDispatch } from "react-redux";
import { handleUpdateSequence } from "store/activities/thunks";
import * as Sentry from '@sentry/react';

// Determine which backend to use based on device type
const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

// Define the drag item type
const ITEM_TYPE = "assignment";

// Draggable assignment component
const DraggableAssignment = ({
  assignment,
  index,
  moveAssignment,
  page,
  pageId,
  bookId,
  onDragEnd,
  draftId,
  setDraftId,
  onDraftDeleted,
  onAdminAssignmentRefetch,
  onDraftSaved,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: assignment.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        onDragEnd();
      }
    },
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveAssignment(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`assignment-item ${isDragging ? "dragging" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="assignment-content">
        <Assignment
          pageSrc={get(page, "resource")}
          pageId={pageId}
          bookId={bookId}
          assignment={assignment}
          draft={assignment?.draft}
          draftId={draftId}
          setDraftId={setDraftId}
          onDraftDeleted={onDraftDeleted}
          onAdminAssignmentRefetch={onAdminAssignmentRefetch}
          onDraftSaved={onDraftSaved}
        />
      </div>
    </div>
  );
};

const AssignmentList = ({ pageActivities, page, pageId, bookId, draftId, setDraftId, onDraftDeleted, onAdminAssignmentRefetch, onDraftSaved }) => {
  const [activities, setActivities] = useState([]);
  const isTouch = useMemo(() => isTouchDevice(), []);
  const dispatch = useDispatch();

  useEffect(() => {
    try{
      const initializedActivities = pageActivities.map((activity, index) => ({
        ...activity,
        sequenceId: activity.sequenceId ?? index + 1,
      }));
  
      const sortedActivities = [...initializedActivities].sort((a, b) => {
        const seqA = parseInt(a.sequence_order) || 0;
        const seqB = parseInt (b.sequence_order) || 0;
        return seqA - seqB;
      });
  
      setActivities(sortedActivities);
    }catch(error){
      Sentry.captureException(`275 Error initializing activities: ${error}`);
      console.log(error);
    }

  }, [pageActivities]);


  const moveAssignment = useCallback((fromIndex, toIndex) => {
    setActivities((prevActivities) => {
      const result = [...prevActivities];
      const [movedItem] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, movedItem);

      const updatedActivities = result.map((activity, index) => ({
        ...activity,
        sequence: index + 1,
      }));

      return updatedActivities;
    });
  }, []);


  const handleDragEnd = useCallback(async () => {
    try {
      const payload = {
        activity: activities?.map((element,index) => {
          return {
            id: element.id,
            type: element.activity_type,
            sequence: element?.sequence,
          };
        })
      }
      await dispatch(handleUpdateSequence(payload));
    } catch (error) {
      console.error('Failed to update sequence:', error);
      Sentry.captureException(`276 Error in handleDragEnd: ${error}`);
    }
  }, [activities, dispatch]);

  if (activities?.length === 0) return;
  
  return (
      <div className="assignments-container">
        {map(activities, (activity, index) => (
          <DraggableAssignment
            key={activity.id || activity.docId}
            assignment={activity}
            index={index}
            moveAssignment={moveAssignment}
            page={page}
            pageId={pageId}
            bookId={bookId}
            onDragEnd={handleDragEnd}
            draftId={draftId} 
            setDraftId={setDraftId}
            onDraftDeleted={onDraftDeleted}
            onAdminAssignmentRefetch={onAdminAssignmentRefetch}
            onDraftSaved={onDraftSaved}
          />
        ))}
      </div>
  );
};

// Main component to replace your current mapping code
const AssignmentAreaWithDnd = ({ pageActivities, page, pageId, bookId, draftId, setDraftId, onDraftDeleted, onAdminAssignmentRefetch, onDraftSaved }) => {
  return (
    <AssignmentList
      pageActivities={pageActivities}
      page={page}
      pageId={pageId}
      bookId={bookId}
      draftId={draftId} 
      setDraftId={setDraftId}
      onDraftDeleted={onDraftDeleted}
      onAdminAssignmentRefetch={onAdminAssignmentRefetch}
      onDraftSaved={onDraftSaved}
    />
  );
};

// CSS to add to your stylesheet
const styles = `
.assignments-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.assignment-item {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 4px;
  transition: all 200ms ease;
  cursor: move;
}

.assignment-item.dragging {
  background-color: #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.drag-handle {
  color: #888;
  font-size: 20px;
  padding: 12px;
  user-select: none;
  touch-action: none;
}

.assignment-content {
  flex: 1;
}
`;

export default AssignmentAreaWithDnd;
